import React, { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw, Radio, Search } from "lucide-react";
import { getSignals } from "../services/signals";
import SignalCard from "../components/SignalCard";
import { CategoryTabs } from "../components/signals/TradePairSelector";
import { Skeleton } from "../components/common/Card";
import Reveal from "../components/common/Reveal";
import ChartBackdrop from "../components/common/ChartBackdrop";

function CountdownBar({ seconds, pct }) {
  return (
    <div className="surface mt-6 flex items-center gap-4 rounded-2xl px-5 py-4">
      <span className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">Next refresh</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/6">
        <div className="h-full rounded-full bg-[#d4d8dc] transition-all duration-1000 ease-linear" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-sm font-semibold tabular-nums text-white" data-testid="signals-countdown">{seconds}s</span>
    </div>
  );
}

export default function Signals() {
  const [activeCategory, setActiveCategory] = useState("currencies");
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seconds, setSeconds] = useState(60);
  const [searchQuery, setSearchQuery] = useState("");

  const categoryCacheRef = useRef({});
  const activeCategoryRef = useRef(activeCategory);
  const isRefreshingRef = useRef(false);

  // Persistent map tracking the last displayed direction for each pair by name
  const pairDirectionsRef = useRef({});

  useEffect(() => {
    activeCategoryRef.current = activeCategory;
  }, [activeCategory]);

  const load = useCallback(async (catToLoad, isRefresh = false) => {
    const category = catToLoad || activeCategoryRef.current;

    // Prevent concurrent duplicate refresh triggers
    if (isRefresh && isRefreshingRef.current) return;
    if (isRefresh) isRefreshingRef.current = true;

    // Instant cache display if available and not a refresh
    if (!isRefresh && categoryCacheRef.current[category]) {
      setSignals(categoryCacheRef.current[category]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // 1. Fetch fresh signal data from the ORIGINAL system (dynamic percentages, prices, timeframes, confidence)
      const data = await getSignals(category);
      const items = data.signals || [];

      // 2. Map each pair and apply flip logic:
      // If this is a refresh, every pair MUST flip relative to its own previous displayed direction:
      // UP -> DOWN, DOWN -> UP
      // If pair has no previous record (first load), use the original generated direction.
      const processedItems = items.map((signal) => {
        const pairKey = signal.pair;
        const prevDirection = pairDirectionsRef.current[pairKey];

        let finalDirection = signal.direction;

        if (isRefresh && prevDirection) {
          finalDirection = prevDirection === "UP" ? "DOWN" : "UP";
        } else if (prevDirection) {
          // If already recorded and switching tabs without refresh, maintain recorded direction
          finalDirection = prevDirection;
        } else {
          // First time seeing this pair on initial load
          finalDirection = signal.direction;
        }

        pairDirectionsRef.current[pairKey] = finalDirection;

        return {
          ...signal,
          direction: finalDirection,
        };
      });

      categoryCacheRef.current[category] = processedItems;

      if (activeCategoryRef.current === category) {
        setSignals(processedItems);
      }
    } catch (e) {
      // Fallback
    } finally {
      setLoading(false);
      isRefreshingRef.current = false;
    }
  }, []);

  // Initial load and category change
  useEffect(() => {
    load(activeCategory, false);
  }, [activeCategory, load]);

  // Automatic 60-second refresh
  const handleCountdownExpire = useCallback(() => {
    load(activeCategoryRef.current, true);
  }, [load]);

  const onExpireRef = useRef(handleCountdownExpire);
  useEffect(() => {
    onExpireRef.current = handleCountdownExpire;
  }, [handleCountdownExpire]);

  // Existing 60-second timer countdown
  useEffect(() => {
    const t = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // When timer reaches 0, trigger auto refresh and reset countdown to 60
  useEffect(() => {
    if (seconds === 0) {
      if (onExpireRef.current) {
        onExpireRef.current();
      }
      setSeconds(60);
    }
  }, [seconds]);

  const handleSelectCategory = useCallback((cat) => {
    setActiveCategory(cat);
    if (categoryCacheRef.current[cat]) {
      setSignals(categoryCacheRef.current[cat]);
      setLoading(false);
    } else {
      setSignals([]);
      setLoading(true);
    }
  }, []);

  const pct = (seconds / 60) * 100;

  const filteredSignals = signals.filter((signal) =>
    String(signal.pair || "").toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  return (
    <div className="relative mx-auto max-w-6xl pb-12">
      <ChartBackdrop className="!fixed" opacity={0.08} />
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
              <Radio size={14} /> Signal Room
            </div>
            <h1 className="font-head text-2xl font-bold uppercase tracking-tight text-white md:text-3xl">AI Signals</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={true}
              aria-disabled="true"
              className="btn-ghost sweep flex items-center gap-2 rounded-xl px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest pointer-events-none cursor-default select-none"
              data-testid="signals-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>
      </Reveal>

      {/* Isolated Countdown Bar using same timer state */}
      <CountdownBar seconds={seconds} pct={pct} />

      {/* Signal Pair Search */}
      <div className="surface mt-6 flex items-center gap-3 rounded-2xl px-5 py-4">
        <Search size={17} className="shrink-0 text-neutral-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search signal pair..."
          className="w-full bg-transparent font-mono text-sm text-white outline-none placeholder:text-neutral-600"
          aria-label="Search signal pair"
          data-testid="signals-search-input"
        />
      </div>

      {/* 4 Category Tabs */}
      <CategoryTabs
        activeCategory={activeCategory}
        onSelectCategory={handleSelectCategory}
      />

      {/* Signal Cards Grid for all instruments in active category */}
      <div className="mt-6">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {loading && signals.length === 0
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="surface rounded-2xl p-5">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="mt-3 h-3 w-16" />
                  <Skeleton className="mt-6 h-8 w-full" />
                  <Skeleton className="mt-4 h-1.5 w-full" />
                </div>
              ))
            : filteredSignals.length > 0
              ? filteredSignals.map((s, i) => (
                  <SignalCard key={s.id || `${s.pair}-${i}`} signal={s} index={i} />
                ))
              : (
                  <div className="surface col-span-full rounded-2xl px-6 py-12 text-center">
                    <Search size={22} className="mx-auto mb-3 text-neutral-600" />
                    <p className="font-mono text-xs uppercase tracking-widest text-neutral-500">
                      No matching signals found
                    </p>
                  </div>
                )}
        </div>
      </div>

      <p className="mt-8 text-center text-[11px] text-neutral-600">
        Live crypto signals use real-time market prices with momentum & RSI indicators; other pairs are algorithmic signals. Not financial advice. Trading involves risk — past performance does not guarantee future results.
      </p>
    </div>
  );
}


